import { STATUS } from "./statuses";

// Helper to safely get env variable (works in both app and Cypress)
export const getApiBaseUrl = () => {
  // Check if we're in a browser environment with import.meta
  if (import.meta?.env) {
    return (
      import.meta.env.VITE_API_BASE_URL ||
      "https://api.oxygen.businessfundamentals.nl"
    );
  }
  // Fallback for Cypress or other environments
  return "https://api.oxygen.businessfundamentals.nl";
};

const API = {
  BASE: `${getApiBaseUrl()}/api/v1`,
  AUTH: {
    ACCESS: "/auth/access",
    // ActiveDirectory tenants.
    // AD_TOKEN is the silent Windows-integrated sign-in
    // AD_ACCESS is the AD username/password fallback (POST)
    AD_TOKEN: "/auth/ad/token",
    AD_ACCESS: "/auth/ad/access",
    // Anonymous — the interceptor skips auth for any /auth/ path. Returns the
    // tenant's name, theme (branding JSON), logo (logoUrl/mediaUrl) and
    // authenticationMode; fetched once at app startup (useTenantContextStore).
    TENANT_CONTEXT: "/auth/tenant/context",
    REFRESH: "/auth/refresh",
    CHANGE_PASSWORD: "/auth/changepassword",
    FORGOT_PASSWORD: "/auth/passwordforgotten",
    RESET_PASSWORD: "/auth/resetpassword",
    SIGN_OUT: "/auth/signout",
    REGISTER: "/auth/register",
  },
  BACKEND: {
    COUNTRIES: "/admin/countries",
    COUNTRIES_DROPDOWN: "/admin/countries/dropdown",
    FEATURES: "/admin/features/tree",
    USERS: "/admin/users",
    USERS_ROLODEX: "/admin/users/rolodex",
    USERS_INFO: "/admin/users/info",
    // Pending self-registrations. Same GetUserRolodexResponse shape as
    // USERS_ROLODEX, but the endpoint forces Status=Pending server-side. Its
    // per-item base (get/update/delete) reuses the standard users endpoint.
    PENDINGUSERS: "/admin/users",
    PENDINGUSERS_ROLODEX: "/admin/users/pending",
    GROUPS_ROLODEX: "/admin/groups/rolodex",
    GROUPS_INFO: "/admin/groups/info",
    GROUPS: "/admin/groups",
    GROUPS_DROPDOWN: "/admin/groups/dropdown",
    DOCUMENT_CATEGORIES_TREE: "/admin/documentcategories/tree",
    DOCUMENT_CATEGORIES_ROLODEX: "/admin/documentcategories/rolodex",
    DOCUMENT_CATEGORIES_INFO: "/admin/documentcategories/info",
    DOCUMENT_CATEGORIES_DROPDOWN: "/admin/documentcategories/dropdown/status",
    DOCUMENT_CATEGORIES: "/admin/documentcategories",
    DOCUMENTS_ROLODEX: "/admin/documents/rolodex",
    DOCUMENTS_INFO: "/admin/documents/info",
    DOCUMENTS: "/admin/documents",
    IMAGE_CATEGORIES_TREE: "/admin/imagecategories/tree",
    IMAGE_CATEGORIES_ROLODEX: "/admin/imagecategories/rolodex",
    IMAGE_CATEGORIES_INFO: "/admin/imagecategories/info",
    IMAGE_CATEGORIES_DROPDOWN: "/admin/imagecategories/dropdown/status",
    IMAGE_CATEGORIES: "/admin/imagecategories",
    IMAGES_ROLODEX: "/admin/images/rolodex",
    IMAGES_INFO: "/admin/images/info",
    IMAGES: "/admin/images",
    SCHEMATICS_ROLODEX: "/admin/schematics/rolodex",
    SCHEMATICS_INFO: "/admin/schematics/info",
    // Read by asset — the normal way to resolve an asset's schematic.
    // A 404 means the asset has no schematic, not a failure.
    SCHEMATICS_ASSET: "/admin/schematics/asset",
    SCHEMATICS: "/admin/schematics",
    VIDEO_CATEGORIES_TREE: "/admin/videocategories/tree",
    VIDEO_CATEGORIES_ROLODEX: "/admin/videocategories/rolodex",
    VIDEO_CATEGORIES_INFO: "/admin/videocategories/info",
    VIDEO_CATEGORIES_DROPDOWN: "/admin/videocategories/dropdown/status",
    VIDEO_CATEGORIES: "/admin/videocategories",
    VIDEOS_ROLODEX: "/admin/videos/rolodex",
    VIDEOS_INFO: "/admin/videos/info",
    VIDEOS: "/admin/videos",
    AUDIO_CATEGORIES_TREE: "/admin/audiocategories/tree",
    AUDIO_CATEGORIES_ROLODEX: "/admin/audiocategories/rolodex",
    AUDIO_CATEGORIES_INFO: "/admin/audiocategories/info",
    AUDIO_CATEGORIES_DROPDOWN: "/admin/audiocategories/dropdown/status",
    AUDIO_CATEGORIES: "/admin/audiocategories",
    AUDIOS_ROLODEX: "/admin/audios/rolodex",
    AUDIOS_INFO: "/admin/audios/info",
    AUDIOS: "/admin/audios",
    NEWS_CATEGORIES_TREE: "/admin/newscategories/tree",
    NEWS_CATEGORIES_ROLODEX: "/admin/newscategories/rolodex",
    NEWS_CATEGORIES_INFO: "/admin/newscategories/info",
    NEWS_CATEGORIES_DROPDOWN: "/admin/newscategories/dropdown/status",
    NEWS_CATEGORIES: "/admin/newscategories",
    APP_CATEGORIES_TREE: "/admin/appcategories/tree",
    APP_CATEGORIES_ROLODEX: "/admin/appcategories/rolodex",
    APP_CATEGORIES_INFO: "/admin/appcategories/info",
    APP_CATEGORIES_DROPDOWN: "/admin/appcategories/dropdown/status",
    APP_CATEGORIES: "/admin/appcategories",
    ASSET_FRAGMENT_CATEGORIES_TREE: "/admin/assetfragmentcategories/tree",
    ASSET_FRAGMENT_CATEGORIES_ROLODEX: "/admin/assetfragmentcategories/rolodex",
    ASSET_FRAGMENT_CATEGORIES_INFO: "/admin/assetfragmentcategories/info",
    ASSET_FRAGMENT_CATEGORIES_DROPDOWN:
      "/admin/assetfragmentcategories/dropdown/status",
    ASSET_FRAGMENT_CATEGORIES: "/admin/assetfragmentcategories",
    TAGS_ROLODEX: "/admin/tags/rolodex",
    TAGS_INFO: "/admin/tags/info",
    TAGS: "/admin/tags",
    TAGS_DROPDOWN: "/admin/tags/dropdown",
    ZONES: "/admin/zones",
    ZONES_ROLODEX: "/admin/zones/rolodex",
    ZONES_INFO: "/admin/zones/info",
    WIDGETS: "/admin/widgets",
    WIDGETS_ROLODEX: "/admin/widgets/rolodex",
    WIDGETS_INFO: "/admin/widgets/info",
    ASSETS: "/admin/assets",
    ASSETS_ROOTS: "/admin/assets/roots",
    // Bespoke asset-relation operations (ADR-0050): body { assetID, sourceID, destinationPath }.
    ASSET_RELATION_MOVE: "/admin/assetrelation/moveasset",
    ASSET_RELATION_MIRROR: "/admin/assetrelation/mirrorasset",
    // Reorder among siblings: body { assetID, sourceID: parent id, rank: predecessor's rank | null }.
    ASSET_RELATION_RERANK: "/admin/assetrelation/rerankasset",
    ASSET_VERSIONS: "/admin/assetversions",
    ASSET_VERSIONS_INFO: "/admin/assetversions/info",
    ASSETTEMPLATES: "/admin/assettemplates",
    ASSETTEMPLATES_ROLODEX: "/admin/assettemplates/rolodex",
    ASSETTEMPLATES_INFO: "/admin/assettemplates/info",
    FRAGMENTS: "/admin/assetfragments",
    FRAGMENTS_ROLODEX: "/admin/assetfragments/rolodex",
    FRAGMENTS_INFO: "/admin/assetfragments/info",
    HELP: "/admin/helptopics",
    HELP_TREE: "/admin/helptopics/tree",
    HELP_INFO: "/admin/helptopics/info",
    DECISIONTREES: "/admin/decisiontrees",
    DECISIONTREES_ROLODEX: "/admin/decisiontrees/rolodex",
    DECISIONTREES_INFO: "/admin/decisiontrees/info",
    CONTACTS_ROLODEX: "/admin/contacts/rolodex",
    CONTACTS_INFO: "/admin/contacts/info",
    CONTACTS: "/admin/contacts",
    // Glossary (ADR-0068). Meanings rerank: POST {MEANINGS}/{id}/rerank?rank=&termID=
    TERMS: "/admin/terms",
    TERMS_ROLODEX: "/admin/terms/rolodex",
    TERMS_INFO: "/admin/terms/info",
    TERMS_DROPDOWN: "/admin/terms/dropdown",
    MEANINGS: "/admin/meanings",
    MEANINGS_ROLODEX: "/admin/meanings/rolodex",
    MEANINGS_INFO: "/admin/meanings/info",
    TERMSYNONYMS: "/admin/termsynonyms",
    TERMSYNONYMS_ROLODEX: "/admin/termsynonyms/rolodex",
    TERMSYNONYMS_INFO: "/admin/termsynonyms/info",
    TERMSYNONYMTYPES: "/admin/termsynonymtypes",
    TERMSYNONYMTYPES_DROPDOWN: "/admin/termsynonymtypes/dropdown",
    NEWS_ROLODEX: "/admin/news/rolodex",
    NEWS_INFO: "/admin/news/info",
    NEWS: "/admin/news",
    NOTIFICATIONS_ROLODEX: "/admin/notifications/rolodex",
    NOTIFICATIONS_INFO: "/admin/notifications/info",
    NOTIFICATIONS: "/admin/notifications",
    FEEDBACKS_ROLODEX: "/admin/feedbacks/rolodex",
    FEEDBACKS: "/admin/feedbacks",
    REPORTS_AUTHENTICATION_ROLODEX: "/admin/authenticationaudits/rolodex",
    REPORTS_AUTHENTICATION_GRAPH: "/admin/authenticationaudits/graph",
    REPORTS_CARETAKER_ROLODEX: "/admin/caretakeraudits/rolodex",
    REPORTS_CARETAKER_GRAPH: "/admin/caretakeraudits/graph",
    REPORTS_CHANGE_ROLODEX: "/admin/changeaudits/rolodex",
    REPORTS_CHANGE_GRAPH: "/admin/changeaudits/graph",
    REPORTS_VIEW_ROLODEX: "/admin/viewaudits/rolodex",
    REPORTS_VIEW_GRAPH: "/admin/viewaudits/graph",
    REPORTS_LINK_ROLODEX: "/admin/linkaudits/rolodex",
    REPORTS_SEARCH_ROLODEX: "/admin/searchaudits/rolodex",
    REPORTS_SEARCH_GRAPH: "/admin/searchaudits/graph",
    SEARCH_INDEX: "/admin/search-index",
    TENANTS: "/admin/tenants",
    TENANTS_ROLODEX: "/admin/tenants/rolodex",
    TENANTS_INFO: "/admin/tenants/info",
    SETTINGS: "/admin/settings",
    BANNER: "/admin/banner",
    APPS: "/admin/apps",
    FAVOURITE_COLLECTION: "/admin/favouritecollections",
    FAVOURITE: "/admin/favourites",
    TOOLBOX_NLV: "/admin/toolbox/nlv",
  },

  // Frontend/Client endpoints
  FRONTEND: {
    // Documents
    DOCUMENTS: "/client/documents",
    DOCUMENTS_ALL: "/client/documents/rolodex/all",
    DOCUMENTS_BOOKMARKS: "/client/documents/rolodex/bookmark",
    DOCUMENTS_POPULAR: "/client/documents/rolodex/popular",
    DOCUMENTS_MOST_VIEWED: "/client/documents/rolodex/mostviewed",
    DOCUMENTS_RECENTLY_CHANGED: "/client/documents/rolodex/recentlychanged",
    DOCUMENT_CATEGORIES_TREE: "/client/documentcategories/tree",
    DOCUMENT_COMMENTS: "/client/documentcomments",

    // Images
    IMAGES: "/client/images",
    IMAGES_ALL: "/client/images/rolodex/all",
    IMAGES_BOOKMARKS: "/client/images/rolodex/bookmark",
    IMAGES_POPULAR: "/client/images/rolodex/popular",
    IMAGES_RECOMMENDED: "/client/images/rolodex/recommended",
    IMAGES_MOST_VIEWED: "/client/images/rolodex/mostviewed",
    IMAGES_RECENTLY_CHANGED: "/client/images/rolodex/recentlychanged",
    IMAGE_CATEGORIES_TREE: "/client/imagecategories/tree",
    IMAGE_COMMENTS: "/client/imagecomments",

    // Videos
    VIDEOS: "/client/videos",
    VIDEOS_ROLODEX: "/client/videos/rolodex",
    VIDEOS_ALL: "/client/videos/rolodex/all",
    VIDEOS_BOOKMARKS: "/client/videos/rolodex/bookmark",
    VIDEOS_POPULAR: "/client/videos/rolodex/popular",
    VIDEOS_RECOMMENDED: "/client/videos/rolodex/recommended",
    VIDEOS_MOST_VIEWED: "/client/videos/rolodex/mostviewed",
    VIDEOS_RECENTLY_CHANGED: "/client/videos/rolodex/recentlychanged",
    VIDEO_CATEGORIES_TREE: "/client/videocategories/tree",
    VIDEO_COMMENTS: "/client/videocomments",

    // Audio
    AUDIOS: "/client/audios",
    AUDIOS_ROLODEX: "/client/audios/rolodex",
    AUDIOS_ALL: "/client/audios/rolodex/all",
    AUDIOS_BOOKMARKS: "/client/audios/rolodex/bookmark",
    AUDIOS_POPULAR: "/client/audios/rolodex/popular",
    AUDIOS_RECOMMENDED: "/client/audios/rolodex/recommended",
    AUDIOS_MOST_VIEWED: "/client/audios/rolodex/mostviewed",
    AUDIOS_RECENTLY_CHANGED: "/client/audios/rolodex/recentlychanged",
    AUDIO_CATEGORIES_TREE: "/client/audiocategories/tree",
    AUDIO_COMMENTS: "/client/audiocomments",

    // News
    NEWS: "/client/news",
    NEWS_ALL: "/client/news/rolodex/all",
    NEWS_BOOKMARKS: "/client/news/rolodex/bookmark",
    NEWS_POPULAR: "/client/news/rolodex/popular",
    NEWS_RECOMMENDED: "/client/news/rolodex/recommended",
    NEWS_MOST_VIEWED: "/client/news/rolodex/mostviewed",
    NEWS_RECENTLY_CHANGED: "/client/news/rolodex/recentlychanged",
    NEWS_CATEGORIES_TREE: "/client/newscategories/tree",
    NEWS_COMMENTS: "/client/newscomments",

    // Zones (for tab navigation)
    ZONES: "/client/zones",

    // Assets
    ASSETS: "/client/assets",
    ASSETS_ROOTS: "/client/assets/roots",
    ASSET_COMMENTS: "/client/assetcomments",

    // Notifications
    NOTIFICATIONS_ME: "/client/users/me/notifications",

    // Help
    HELP_TREE: "/client/helptopics/tree",
    HELP: "/client/helptopics",

    // Contacts
    CONTACTS: "/client/contacts",
    CONTACTS_ROLODEX: "/client/contacts/rolodex",

    // Contact page (tenant's own contact details, distinct from the CONTACTS directory)
    CONTACT: "/client/contact",

    // Glossary (ADR-0068) — published terms and meanings only
    TERMS: "/client/terms",
    TERMS_ROLODEX: "/client/terms/rolodex",
    MEANINGS: "/client/meanings",
    MEANINGS_ROLODEX: "/client/meanings/rolodex",
    TERMSYNONYMS: "/client/termsynonyms",
    TERMSYNONYMS_ROLODEX: "/client/termsynonyms/rolodex",
    TERMSYNONYMTYPES: "/client/termsynonymtypes",

    // User Profile
    USER_PROFILE: "/client/users/me/profile",

    // Feedback
    FEEDBACKS: "/client/feedbacks",

    // Widgets
    WIDGETS: "/client/widgets",

    // User Preferences (for widget layouts, settings, etc.)
    USER_PREFERENCES: "/client/users/me/preferences",

    // Apps
    APPS: "/client/apps",

    // Favourite Collections
    USER_FAVOURITE_COLLECTION: "/client/users/me/favouritecollections",
    FAVOURITE_COLLECTION: "/client/favouritecollections",
    FAVOURITE: "/client/favourites",

    // Audits
    VIEW_AUDITS: "/client/viewaudits",
    SEARCH_AUDITS: "/client/searchaudits",

    //Accreditation
    ACCREDITATION: "/client/accreditation",

    // Modeling — reader access (role Member) to published records only; there
    // is no status segment, an unpublished or inaccessible record is a 404.
    DECISIONTREES: "/client/decisiontrees",
    // GET /client/schematics/asset/{assetID}: the diagram attached to an asset
    // (same fields as the admin by-asset route; 404 = no diagram for the reader).
    SCHEMATICS_ASSET: "/client/schematics/asset",
  },

  SEARCH: "/search/search",
};

/**
 * Build a URL with status filter
 * @param {string} endpoint - The API endpoint path
 * @param {number} status - Status code (default: STATUS.PUBLISHED)
 * @returns {string} URL with status suffix
 *
 * @example
 * withStatus(API.BACKEND.GROUPS_DROPDOWN, STATUS.ALL)  // "/admin/groups/dropdown/status/0"
 * withStatus(API.BACKEND.FEATURES)                     // "/admin/features/status/4"
 *
 * Note: not every endpoint supports a /status/ segment — SETTINGS does not
 * (the backend has no settings GET at all). Check the endpoint first.
 */
export const withStatus = (endpoint, status = STATUS.PUBLISHED) => {
  return `${endpoint}/status/${status}`;
};

/**
 * Build a full URL with base and status filter
 * @param {string} endpoint - The API endpoint path
 * @param {number} status - Status code (default: STATUS.PUBLISHED)
 * @returns {string} Full URL with base and status suffix
 *
 * @example
 * apiWithStatus(API.BACKEND.GROUPS_DROPDOWN, STATUS.ALL)  // "https://api.../api/v1/admin/groups/dropdown/status/0"
 * apiWithStatus(API.BACKEND.FEATURES)                     // "https://api.../api/v1/admin/features/status/4"
 *
 * Note: not every endpoint supports a /status/ segment — SETTINGS does not
 * (the backend has no settings GET at all). Check the endpoint first.
 */
export const apiWithStatus = (endpoint, status = STATUS.PUBLISHED) => {
  return `${API.BASE}${endpoint}/status/${status}`;
};

/**
 * Build a URL for fetching a single item by ID with status filter
 * @param {string} endpoint - The API endpoint path
 * @param {string|number} id - The item ID
 * @param {number} status - Status code (default: STATUS.ALL)
 * @returns {string} Full URL with base, id, and status suffix
 *
 * @example
 * apiItemWithStatus(API.BACKEND.USERS, 123)              // "https://api.../api/v1/admin/users/123/status/0"
 * apiItemWithStatus(API.BACKEND.USERS, 123, STATUS.PUBLISHED)  // "https://api.../api/v1/admin/users/123/status/4"
 */
export const apiItemWithStatus = (endpoint, id, status = STATUS.ALL) => {
  return `${API.BASE}${endpoint}/${id}/status/${status}`;
};

/**
 * Build a URL for fetching item info by ID
 * @param {string} endpoint - The API endpoint path
 * @param {string|number} id - The item ID
 * @returns {string} Full URL with base, id, and /info suffix
 *
 * @example
 * apiItemInfo(API.BACKEND.ASSETS, "my-asset")           // "https://api.../api/v1/admin/assets/my-asset/info"
 * apiItemInfo(API.BACKEND.FRAGMENTS, 123)               // "https://api.../api/v1/admin/assetfragments/123/info"
 */
export const apiItemInfo = (endpoint, id) => {
  return `${API.BASE}${endpoint}/${id}/info`;
};

/**
 * Build a URL for fetching entity-level info (form config, statuses, etc.)
 * @param {string} endpoint - The API endpoint path
 * @returns {string} Full URL with base and /info suffix
 *
 * @example
 * apiInfo(API.BACKEND.DOCUMENTS)   // "https://api.../api/v1/admin/documents/info"
 * apiInfo(API.BACKEND.IMAGES)      // "https://api.../api/v1/admin/images/info"
 */
export const apiInfo = (endpoint) => {
  return `${API.BASE}${endpoint}/info`;
};

/**
 * Build the URL to approve a pending self-registration. Sets Status=Published
 * and Role to the given value (which must be a valid Role other than NoAccess);
 * the backend emails the user that they've been approved.
 * @param {string|number} id - The pending user's ID
 * @returns {string} Full URL, e.g. "https://api.../api/v1/admin/users/120/approve"
 */
export const apiUserApprove = (id) =>
  `${API.BASE}${API.BACKEND.USERS}/${id}/approve`;

/**
 * Build the URL to deny a pending self-registration. Trashes the user
 * (reversible via the standard restore endpoint); the backend emails the user
 * that they were not approved.
 * @param {string|number} id - The pending user's ID
 * @returns {string} Full URL, e.g. "https://api.../api/v1/admin/users/120/deny"
 */
export const apiUserDeny = (id) => `${API.BASE}${API.BACKEND.USERS}/${id}/deny`;

/**
 * Build the URL to replace a departing user with a successor (ADR-0055).
 * PUT with body { newUserID }: moves group memberships, notification
 * subscriptions, favourite collections, comments and views from user {id} to
 * the successor in one transaction. User {id}'s own row is untouched — deleting
 * it stays the separate Delete step. The backend requires both users to exist,
 * to differ, and the successor to be Published; it answers 204, 404 or 422
 * (ProblemDetails) and writes one AuthenticationAudit row (Action = Replace).
 * @param {string|number} id - The departing user's ID
 * @returns {string} Full URL, e.g. "https://api.../api/v1/admin/users/120/replace"
 */
export const apiUserReplace = (id) =>
  `${API.BASE}${API.BACKEND.USERS}/${id}/replace`;

/**
 * Build the URL to toggle a feature on/off. A body-less PUT that flips the
 * feature's enabled state (ADR feature-toggle endpoint), returning 204.
 * @param {string|number} id - The feature's ID
 * @returns {string} Full URL, e.g. "https://api.../api/v1/admin/features/1/toggle"
 */
export const apiFeatureToggle = (id) =>
  `${API.BASE}/admin/features/${id}/toggle`;

// Re-export STATUS for convenience
export { STATUS } from "./statuses";

export default API;
